import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProfessionalReviewStats } from '../../../model/client/client-review-model';

interface ReviewStatsProps {
  stats: ProfessionalReviewStats;
}

/**
 * Composant pour afficher les statistiques des avis
 */
const ReviewStats: React.FC<ReviewStatsProps> = ({ stats }) => {
  const { t } = useTranslation();
  
  // Fonction pour afficher les étoiles
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <svg 
            key={i} 
            className={`h-5 w-5 ${i < Math.round(rating) ? 'text-yellow-400' : 'text-gray-300'}`} 
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
      <div className="flex flex-col md:flex-row items-center justify-center gap-8">
        {/* Note moyenne */}
        <div className="text-center">
          <div className="text-4xl font-bold text-primary mb-2" itemProp="ratingValue">
            {stats.averageRating.toFixed(1)}
          </div>
          <div className="mb-2">
            {renderStars(stats.averageRating)}
          </div>
          <div className="text-sm text-gray-500" itemProp="reviewCount">
            {t('reviews.basedOn', { count: stats.count })}
          </div>
        </div>
        
        {/* Distribution des notes */}
        <div className="flex-1 max-w-xs">
          <h3 className="text-sm font-medium text-gray-700 mb-3 text-center md:text-left">
            {t('reviews.ratingDistribution')}
          </h3>
          <div>
            {[5, 4, 3, 2, 1].map(rating => {
              const count = stats.ratingDistribution[rating] || 0;
              const percentage = stats.count > 0 ? (count / stats.count) * 100 : 0;
              
              return (
                <div key={rating} className="flex items-center gap-2 mb-2">
                  <div className="flex items-center gap-1">
                    <span>{rating}</span>
                    <svg className="h-4 w-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full" 
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 w-8 text-right">
                    {count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStats;
